import { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Box,
  Container,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSend } from 'react-icons/fi';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import {
  useConversation,
  useConversationMessages,
  sendMessage,
  markConversationAsRead,
} from '../../hooks/useMessages';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatRelativeTime } from '../../utils/formatters';

export default function ConversationDetail() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { conversation, loading: convLoading } = useConversation(conversationId);
  const { messages, loading: msgsLoading } = useConversationMessages(conversationId);
  const toast = useToast();

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const bubbleMine = useColorModeValue('orange.500', 'orange.400');
  const bubbleOther = useColorModeValue('gray.100', 'gray.600');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Mark as read on mount and when new messages arrive
  useEffect(() => {
    if (conversation && currentUser?.uid && conversation.unreadBy?.includes(currentUser.uid)) {
      markConversationAsRead(conversationId, currentUser.uid).catch(() => {});
    }
  }, [conversation, conversationId, currentUser?.uid]);

  const handleSend = async () => {
    if (!reply.trim() || !currentUser || !conversation) return;
    const msg = reply.trim();
    setReply('');
    setSending(true);

    const otherUid = conversation.participants.find((p) => p !== currentUser.uid);

    try {
      await sendMessage(conversationId, {
        senderUid: currentUser.uid,
        senderName: userData?.anonymizedUsername || 'User',
        message: msg,
        recipientUid: otherUid,
      });
    } catch (err) {
      setReply(msg);
      toast({
        title: 'Error sending message',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  if (convLoading || msgsLoading) {
    return <LoadingSpinner message="Loading conversation..." />;
  }

  if (!conversation || !conversation.participants?.includes(currentUser?.uid)) {
    return (
      <Container maxW="xl" py={8} textAlign="center">
        <Text fontSize="lg" fontWeight="medium" mb={4}>Conversation not found</Text>
        <Text
          color="orange.500"
          cursor="pointer"
          onClick={() => navigate('/messages')}
          fontSize="sm"
        >
          Back to Messages
        </Text>
      </Container>
    );
  }

  const otherUid = conversation.participants.find((p) => p !== currentUser?.uid);
  const otherData = conversation.participantData?.[otherUid] || {};
  const otherName = otherData.isFullyAnonymous ? 'Anonymous' : (otherData.username || 'User');

  return (
    <Container maxW="xl" px={0} py={4}>
      <Flex direction="column" h="calc(100vh - 170px)" bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        {/* Header */}
        <HStack
          spacing={3}
          px={4}
          py={3}
          borderBottomWidth="1px"
          borderColor={borderColor}
        >
          <IconButton
            icon={<Icon as={FiArrowLeft} />}
            variant="ghost"
            size="sm"
            aria-label="Back"
            onClick={() => navigate('/messages')}
          />
          <HStack as={Link} to={`/users/${otherUid}`} spacing={2} _hover={{ opacity: 0.7 }}>
            <Avatar
              size="sm"
              src={otherData.isFullyAnonymous ? undefined : otherData.avatarUrl}
              name={otherName}
              bg={otherData.isFullyAnonymous ? 'gray.400' : 'orange.400'}
            />
            <Text fontWeight="semibold" fontSize="md">{otherName}</Text>
          </HStack>
        </HStack>

        {/* Messages area */}
        <Box flex="1" overflowY="auto" px={4} py={3}>
          <VStack spacing={1} align="stretch">
            {messages.length === 0 && (
              <Text textAlign="center" color="gray.400" fontSize="sm" py={8}>
                Say hello!
              </Text>
            )}

            {messages.map((msg, i) => {
              const isMine = msg.senderUid === currentUser?.uid;
              const prevMsg = messages[i - 1];
              const sameSender = prevMsg && prevMsg.senderUid === msg.senderUid;
              const showTimestamp = !prevMsg ||
                (msg.createdAt?.seconds - (prevMsg.createdAt?.seconds || 0)) > 300;

              return (
                <Box key={msg.id}>
                  {showTimestamp && (
                    <Text textAlign="center" fontSize="xs" color="gray.400" py={2}>
                      {formatRelativeTime(msg.createdAt)}
                    </Text>
                  )}
                  <Flex justify={isMine ? 'flex-end' : 'flex-start'} mt={sameSender && !showTimestamp ? 0.5 : 2}>
                    {!isMine && !sameSender && (
                      <Avatar
                        size="xs"
                        src={otherData.isFullyAnonymous ? undefined : otherData.avatarUrl}
                        name={otherName}
                        bg="gray.400"
                        mr={2}
                        mt="2px"
                      />
                    )}
                    {!isMine && sameSender && !showTimestamp && <Box w="28px" mr={2} />}
                    <Box
                      bg={isMine ? bubbleMine : bubbleOther}
                      color={isMine ? 'white' : 'inherit'}
                      px={3}
                      py={2}
                      rounded="2xl"
                      roundedTopLeft={!isMine && sameSender && !showTimestamp ? 'md' : '2xl'}
                      roundedTopRight={isMine && sameSender && !showTimestamp ? 'md' : '2xl'}
                      maxW="75%"
                      wordBreak="break-word"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">{msg.message}</Text>
                    </Box>
                  </Flex>
                </Box>
              );
            })}

            <div ref={messagesEndRef} />
          </VStack>
        </Box>

        {/* Input area */}
        <HStack
          px={4}
          py={3}
          borderTopWidth="1px"
          borderColor={borderColor}
          spacing={2}
        >
          <Input
            placeholder="Message..."
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rounded="full"
            size="md"
            variant="filled"
            _focus={{ bg: 'white', borderWidth: '1px', borderColor: 'gray.300' }}
          />
          <IconButton
            icon={<Icon as={FiSend} />}
            colorScheme="orange"
            rounded="full"
            size="md"
            aria-label="Send"
            onClick={handleSend}
            isLoading={sending}
            isDisabled={!reply.trim()}
          />
        </HStack>
      </Flex>
    </Container>
  );
}
